import logger from '@/utils/logger'
import Vue from 'vue'
import VueRouter from 'vue-router'
import { store } from '../store/store'
import { getComponentImport } from '@/themes/veneer/componentRegistry'

// Lazy-load non-critical components for code splitting
// This reduces initial bundle size and improves performance

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Landing',
    // Use themed LandingPage component (loads WindermereLanding for windermere theme)
    component: () => getComponentImport('LandingPage')(),
    meta: {
      title: 'Welcome - E-Commerce Store',
      requiresAuth: false
    }
  },
  {
    path: '/shop',
    name: 'Shop',
    component: () => import(/* webpackChunkName: "shop" */ '../views/ShopPage.vue'),
    meta: {
      title: 'Shop - Browse Products',
      requiresAuth: false
    }
  },
  {
    path: '/shop/category/:categoryId',
    name: 'ShopByCategory',
    component: () => import(/* webpackChunkName: "shop" */ '../views/ShopPage.vue'),
    props: true,
    meta: {
      title: 'Shop by Category',
      requiresAuth: false
    }
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: () => import(/* webpackChunkName: "checkout" */ '../views/CheckoutFlow.vue'),
    meta: {
      title: 'Checkout',
      requiresAuth: false // Allow guest checkout
    }
  },
  {
    path: '/checkout/:step',
    name: 'CheckoutStep',
    component: () => import(/* webpackChunkName: "checkout" */ '../views/CheckoutFlow.vue'),
    props: true,
    meta: {
      title: 'Checkout',
      requiresAuth: false
    }
  },

  // Feature Flag Test Route (Development)
  {
    path: '/test-flags',
    name: 'FeatureFlagTest',
    component: () => import(/* webpackChunkName: "test-flags" */ '../views/FeatureFlagTest.vue'),
    meta: {
      title: 'Feature Flag Test',
      requiresAuth: false
    }
  },

  // Genius Payment Test Harness (Development)
  {
    path: '/genius-test',
    name: 'GeniusTest',
    component: () => import(/* webpackChunkName: "genius-test" */ '../views/GeniusTest.vue'),
    meta: {
      title: 'Genius Payment Test',
      requiresAuth: false
    }
  },

  // Declining Balance / Account Test Harness (Development)
  {
    path: '/accounttest',
    name: 'AccountTest',
    component: () => import(/* webpackChunkName: "account-test" */ '../views/AccountTest.vue'),
    meta: {
      title: 'Declining Balance Test',
      requiresAuth: false
    }
  },

  // Payment Result Routes
  {
    path: '/payment-success',
    name: 'PaymentSuccess',
    component: () => import(/* webpackChunkName: "payment-success" */ '../views/PaymentSuccess.vue'),
    meta: {
      title: 'Payment Successful',
      requiresAuth: false
    }
  },
  {
    path: '/payment-cancelled',
    name: 'PaymentCancelled',
    component: () => import(/* webpackChunkName: "payment-results" */ '../views/PaymentCancelled.vue'),
    meta: {
      title: 'Payment Cancelled',
      requiresAuth: false
    }
  },
  {
    path: '/payment-error',
    name: 'PaymentError',
    component: () => import(/* webpackChunkName: "payment-results" */ '../views/PaymentError.vue'),
    meta: {
      title: 'Payment Error',
      requiresAuth: false
    }
  },
  {
    path: '/payment-response',
    name: 'PaymentResponse',
    component: () => import(/* webpackChunkName: "payment-results" */ '../views/PaymentResponse.vue'),
    meta: {
      title: 'Processing Payment',
      requiresAuth: false
    }
  },

  // Legacy route redirects for existing functionality
  {
    path: '/legacy',
    name: 'Legacy',
    component: () => import(/* webpackChunkName: "legacy" */ '../components/Start.vue'),
    meta: {
      title: 'Payment Portal',
      requiresAuth: false
    }
  },

  // Admin Routes
  {
    path: '/admin',
    redirect: '/admin/fulfillments'
  },
  {
    path: '/admin/fulfillments',
    name: 'FulfillmentAdmin',
    component: () => import(/* webpackChunkName: "admin" */ '../views/admin/FulfillmentAdmin.vue'),
    meta: {
      title: 'Fulfillment Management - Admin',
      requiresAuth: true,
      requiresAdmin: true  // Must be admin (Employee.IsAdmin = 1)
    }
  },

  // Policy Pages (Windermere compliance - Global Payments requirements)
  {
    path: '/support',
    name: 'Support',
    component: () => import(/* webpackChunkName: "policies" */ '../views/policies/WindermereSupport.vue'),
    meta: { title: 'Help & Support', requiresAuth: false }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import(/* webpackChunkName: "policies" */ '../views/policies/WindermerePrivacy.vue'),
    meta: { title: 'Privacy Policy', requiresAuth: false }
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: () => import(/* webpackChunkName: "policies" */ '../views/policies/WindermereTerms.vue'),
    meta: { title: 'Terms & Conditions', requiresAuth: false }
  },
  {
    path: '/refunds-and-returns',
    name: 'RefundsAndReturns',
    component: () => import(/* webpackChunkName: "policies" */ '../views/policies/WindermereRefunds.vue'),
    meta: { title: 'Refunds & Returns', requiresAuth: false }
  },
  {
    path: '/shipping-policy',
    name: 'ShippingPolicy',
    component: () => import(/* webpackChunkName: "policies" */ '../views/policies/WindermereShipping.vue'),
    meta: { title: 'Pickup & Order Policy', requiresAuth: false }
  },

  // Catch all route - redirect to landing
  {
    path: '*',
    redirect: '/'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // 🚨 CRITICAL FIX: Prevent frontend from intercepting backend API routes
  // This ensures WorldPay callbacks reach the backend instead of being caught by Vue router
  if (to.path.startsWith('/api/')) {
    logger.debug('🔄 API route detected, redirecting to backend:', to.path)
    // Force a full page navigation to the backend API endpoint
    window.location.href = to.fullPath
    return // Don't call next() - we're doing a full page redirect
  }

  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title
  }

  // Check authentication requirements
  if (to.meta.requiresAuth && !store.state.loggedIn) {
    // Store the intended destination for post-login redirect
    store.commit('updateField', { path: 'loginRedirectUrl', value: to.fullPath })
    // Redirect to landing page and show login modal
    store.commit('updateField', { path: 'showLoginModal', value: true })
    next('/')
    return
  }

  // Check admin requirements (must be logged in AND have isAdmin flag)
  if (to.meta.requiresAdmin) {
    const user = store.state.loggedInUser
    if (!user || !user.isAdmin) {
      logger.warn('🚫 Admin access denied - user is not admin:', user?.name)
      store.commit('updateField', {
        path: 'message',
        value: 'Access denied. Administrator privileges required.'
      })
      // Redirect to landing page
      next('/')
      return
    }
    logger.debug('✅ Admin access granted for:', user?.name)
  }

  // Check category-specific authentication for shop routes
  if (to.name === 'ShopByCategory' && to.params.categoryId) {
    const categoryId = parseInt(to.params.categoryId)
    const suppliesCategoryId = 1393

    // If guest user tries to access non-Supplies category, redirect and show login
    if (store.state.isGuestUser && categoryId !== suppliesCategoryId) {
      store.commit('updateField', { path: 'showLoginModal', value: true })
      store.commit('updateField', {
        path: 'message',
        value: 'Please sign in to access this category.'
      })
      next({ path: '/shop', replace: true }) // Use replace to avoid adding to history
      return
    }
  }

  next()
})

// Suppress navigation redirect "errors" - these are expected behavior, not actual errors
// Vue Router 3.x throws when navigation is cancelled/redirected by guards
const originalPush = router.push
router.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    // Ignore navigation duplicated and redirect errors
    if (err && err.name === 'NavigationDuplicated') return
    if (err && err.message && err.message.includes('Redirected')) return
    throw err
  })
}

export default router
